import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Trash2, X, Syringe, Stethoscope, Scissors, Package, Link2, Tag, Search, Edit3, PackagePlus, ToggleLeft, ToggleRight, AlertTriangle, Clock } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import {
  getClinicOperations, createClinicOperation, deleteClinicOperation, updateClinicOperation,
  getProducts, createProduct, updateProduct, deleteProduct,
  getCategories, createCategory, deleteCategory
} from '../services/api';
import BottomNav from '../components/BottomNav';
import Loader from '../components/Loader';
import { toast } from 'sonner';
import { OperationForm } from '../components/stock/OperationForm';
import { ProductForm } from '../components/stock/ProductForm';
import { ProductRow } from '../components/stock/ProductRow';
import { BatchStockModal } from '../components/stock/BatchStockModal';

const TYPE_CONFIG = {
  vaccine: { label: 'Aşı', icon: Syringe, color: 'bg-[#34C759] text-white' },
  treatment: { label: 'Tedavi', icon: Stethoscope, color: 'bg-[#6b6de3] text-white' },
  service: { label: 'Hizmet', icon: Scissors, color: 'bg-[#FF9500] text-white' },
};

const EMPTY_OP_FORM = { name: '', type: 'vaccine', default_price: '', default_interval_days: '', animal_type: 'all', vat_rate: '', duration_minutes: '' };
const EMPTY_PRODUCT_FORM = { name: '', barcode: '', default_price: '', cost_price: '', category_id: '', current_stock: '', min_stock: '', expiry_date: '', expiry_warning_months: 3, vat_rate: '' };

const Stock = () => {
  const [activeTab, setActiveTab] = useState('operations');
  const [loading, setLoading] = useState(true);
  const [operations, setOperations] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [opSearch, setOpSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [opModal, setOpModal] = useState(false);
  const [editOpModal, setEditOpModal] = useState(false);
  const [productModal, setProductModal] = useState(false);
  const [catModal, setCatModal] = useState(false);
  const [linkModal, setLinkModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [batchStockModal, setBatchStockModal] = useState(false);
  const [showLowStock, setShowLowStock] = useState(false);
  const [showExpiring, setShowExpiring] = useState(false);
  const [opForm, setOpForm] = useState({ ...EMPTY_OP_FORM });
  const [editOpForm, setEditOpForm] = useState(null);
  const [productForm, setProductForm] = useState({ ...EMPTY_PRODUCT_FORM });
  const [catName, setCatName] = useState('');
  const [linkOp, setLinkOp] = useState(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [opsR, prodsR, catsR] = await Promise.all([getClinicOperations(), getProducts(), getCategories()]);
      setOperations(opsR.data); setProducts(prodsR.data); setCategories(catsR.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const filteredOps = useMemo(() => {
    if (!opSearch.trim()) return operations;
    const q = opSearch.toLowerCase();
    return operations.filter(o => o.name.toLowerCase().includes(q));
  }, [operations, opSearch]);

  const filteredProducts = useMemo(() => {
    let result = products;
    if (productSearch.trim()) {
      const q = productSearch.toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(q) || (p.barcode && p.barcode.toLowerCase().includes(q)) || (p.category_name && p.category_name.toLowerCase().includes(q)));
    }
    if (showLowStock) {
      result = result.filter(p => p.current_stock <= (p.min_stock || 0));
    }
    if (showExpiring) {
      const warningDate = new Date();
      result = result.filter(p => {
        if (!p.expiry_date) return false;
        const expiry = new Date(p.expiry_date);
        const months = p.expiry_warning_months || 3;
        const threshold = new Date();
        threshold.setMonth(threshold.getMonth() + months);
        return expiry <= threshold;
      });
    }
    return result;
  }, [products, productSearch, showLowStock, showExpiring]);

  const handleCreateOp = async () => {
    if (!opForm.name.trim()) { toast.error('İşlem adı gerekli'); return; }
    try {
      await createClinicOperation({ ...opForm, default_price: opForm.default_price ? parseFloat(opForm.default_price) : 0, default_interval_days: opForm.default_interval_days ? parseInt(opForm.default_interval_days) : null, vat_rate: opForm.vat_rate !== '' ? parseFloat(opForm.vat_rate) : null, duration_minutes: opForm.duration_minutes ? parseInt(opForm.duration_minutes) : null });
      toast.success('İşlem eklendi'); setOpModal(false); setOpForm({ ...EMPTY_OP_FORM }); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
  };

  const handleEditOp = async () => {
    if (!editOpForm || !editOpForm.name.trim()) { toast.error('İşlem adı gerekli'); return; }
    try {
      await updateClinicOperation(editOpForm.id, { name: editOpForm.name, type: editOpForm.type, default_price: editOpForm.default_price ? parseFloat(editOpForm.default_price) : 0, default_interval_days: editOpForm.default_interval_days ? parseInt(editOpForm.default_interval_days) : null, animal_type: editOpForm.animal_type, vat_rate: editOpForm.vat_rate !== '' ? parseFloat(editOpForm.vat_rate) : null, duration_minutes: editOpForm.duration_minutes ? parseInt(editOpForm.duration_minutes) : null });
      toast.success('İşlem güncellendi'); setEditOpModal(false); setEditOpForm(null); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
  };

  const openEditOp = (op) => {
    setEditOpForm({ id: op.id, name: op.name, type: op.type, default_price: op.default_price || '', default_interval_days: op.default_interval_days || '', animal_type: op.animal_type || 'all', vat_rate: op.vat_rate != null ? String(op.vat_rate) : '', duration_minutes: op.duration_minutes || '' });
    setEditOpModal(true);
  };

  const handleDeleteOp = async (id) => { try { await deleteClinicOperation(id); toast.success('İşlem silindi'); loadAll(); } catch (e) { toast.error('Hata'); } };

  const handleCreateProduct = async () => {
    if (!productForm.name.trim()) { toast.error('Ürün adı gerekli'); return; }
    try {
      await createProduct({ ...productForm, category_id: productForm.category_id === 'none' ? null : productForm.category_id, default_price: parseFloat(productForm.default_price) || 0, cost_price: productForm.cost_price ? parseFloat(productForm.cost_price) : null, current_stock: parseInt(productForm.current_stock) || 0, min_stock: parseInt(productForm.min_stock) || 0, expiry_warning_months: parseInt(productForm.expiry_warning_months) || 3, vat_rate: productForm.vat_rate !== '' ? parseFloat(productForm.vat_rate) : null });
      toast.success('Ürün eklendi'); setProductModal(false); setProductForm({ ...EMPTY_PRODUCT_FORM }); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct) return;
    try {
      await updateProduct(editingProduct.id, { name: editingProduct.name, barcode: editingProduct.barcode || null, default_price: parseFloat(editingProduct.default_price) || 0, cost_price: editingProduct.cost_price ? parseFloat(editingProduct.cost_price) : null, category_id: editingProduct.category_id, current_stock: parseInt(editingProduct.current_stock) || 0, min_stock: parseInt(editingProduct.min_stock) || 0, expiry_date: editingProduct.expiry_date || null, expiry_warning_months: parseInt(editingProduct.expiry_warning_months) || 3, vat_rate: editingProduct.vat_rate != null ? parseFloat(editingProduct.vat_rate) : null });
      toast.success('Ürün güncellendi'); setEditingProduct(null); loadAll();
    } catch (e) { toast.error('Hata'); }
  };

  const handleDeleteProduct = async (id) => { try { await deleteProduct(id); toast.success('Ürün silindi'); loadAll(); } catch (e) { toast.error('Hata'); } };

  const handleCreateCategory = async () => { if (!catName.trim()) return; try { await createCategory({ name: catName }); toast.success('Kategori eklendi'); setCatName(''); setCatModal(false); loadAll(); } catch (e) { toast.error('Hata'); } };
  const handleDeleteCategory = async (id) => { try { await deleteCategory(id); toast.success('Kategori silindi'); loadAll(); } catch (e) { toast.error('Hata'); } };

  const handleLinkProduct = async (productId) => {
    if (!linkOp) return;
    const existing = linkOp.linked_products || [];
    if (existing.find(lp => lp.product_id === productId)) { toast.info('Bu ürün zaten bağlı'); return; }
    const prod = products.find(p => p.id === productId);
    const updated = [...existing, { product_id: productId, product_name: prod?.name || '' }];
    try { await updateClinicOperation(linkOp.id, { linked_products: updated }); toast.success('Ürün bağlandı'); loadAll(); setLinkOp(prev => ({ ...prev, linked_products: updated })); } catch (e) { toast.error('Hata'); }
  };

  const handleUnlinkProduct = async (productId) => {
    if (!linkOp) return;
    const updated = (linkOp.linked_products || []).filter(lp => lp.product_id !== productId);
    try { await updateClinicOperation(linkOp.id, { linked_products: updated }); toast.success('Ürün bağlantısı kaldırıldı'); loadAll(); setLinkOp(prev => ({ ...prev, linked_products: updated })); } catch (e) { toast.error('Hata'); }
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.type === 'operation') handleDeleteOp(deleteConfirm.id);
    else if (deleteConfirm.type === 'product') handleDeleteProduct(deleteConfirm.id);
    else if (deleteConfirm.type === 'category') handleDeleteCategory(deleteConfirm.id);
    setDeleteConfirm(null);
  };

  if (loading) return <Loader fullScreen />;

  return (
    <div className="min-h-screen bg-[#F2F2F7] pb-24 lg:pb-4">
      <header className="bg-white px-4 pt-12 lg:pt-4 pb-4 shadow-sm sticky top-0 z-10">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">{`Stok Yönetimi`}</h1>
          <Button onClick={() => setBatchStockModal(true)} className="bg-[#34C759] hover:bg-[#2DA84E] rounded-full h-9 px-4 text-sm font-semibold" data-testid="batch-stock-btn">
            <PackagePlus size={16} className="mr-1.5" /> {`Hızlı Stok`}
          </Button>
        </div>
        <div className="bg-[#F2F2F7] rounded-2xl p-1.5 space-y-1.5">
          {/* Tabs */}
          <div className="grid grid-cols-3 h-9 bg-white rounded-xl p-0.5">
            {[
              { key: 'operations', label: 'İşlemler', icon: Syringe },
              { key: 'products', label: 'Ürünler', icon: Package },
              { key: 'linking', label: 'Bağlantı', icon: Link2 },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex items-center justify-center gap-1 rounded-lg text-[11px] font-semibold transition-all ${activeTab === tab.key ? 'bg-[#6b6de3]/10 text-[#6b6de3]' : 'text-[#8E8E93] hover:text-gray-600'}`} data-testid={`stock-tab-${tab.key}`}>
                <tab.icon size={13} /> {tab.label}
              </button>
            ))}
          </div>
          {/* Search row per tab */}
          {activeTab === 'operations' && (
            <div className="flex gap-1.5">
              <div className="flex-1 relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8E8E93]" />
                <Input value={opSearch} onChange={e => setOpSearch(e.target.value)} placeholder="İşlem ara..." className="h-9 pl-8 text-xs bg-white border-0 rounded-xl" data-testid="op-search-input" />
              </div>
              <Button onClick={() => setOpModal(true)} className="bg-[#6b6de3] hover:bg-[#5658c4] rounded-xl h-9 px-3" data-testid="add-op-btn"><Plus size={16} /></Button>
            </div>
          )}
          {activeTab === 'products' && (
            <>
              <div className="flex gap-1.5">
                <div className="flex-1 relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8E8E93]" />
                  <Input value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Ürün veya barkod ara..." className="h-9 pl-8 text-xs bg-white border-0 rounded-xl" data-testid="product-search-input" />
                </div>
                <Button onClick={() => setProductModal(true)} className="bg-[#6b6de3] hover:bg-[#5658c4] rounded-xl h-9 px-3" data-testid="add-product-btn"><Plus size={16} /></Button>
                <Button onClick={() => setCatModal(true)} variant="outline" className="rounded-xl h-9 px-2.5" data-testid="manage-categories-btn"><Tag size={14} /></Button>
              </div>
              <div className="flex items-center gap-4 bg-white rounded-xl px-3 py-1.5">
                <div className="flex items-center justify-between flex-1" data-testid="low-stock-filter">
                  <span className="text-xs text-[#8E8E93] flex items-center gap-1">
                    <AlertTriangle size={12} className="text-[#FF3B30]" />
                    Stoğu azalanlar
                  </span>
                  <button onClick={() => setShowLowStock(!showLowStock)} data-testid="low-stock-toggle">
                    {showLowStock
                      ? <ToggleRight size={32} className="text-[#FF3B30]" />
                      : <ToggleLeft size={32} className="text-[#C7C7CC]" />}
                  </button>
                </div>
                <div className="w-px h-5 bg-gray-200" />
                <div className="flex items-center justify-between flex-1" data-testid="expiring-filter">
                  <span className="text-xs text-[#8E8E93] flex items-center gap-1">
                    <Clock size={12} className="text-[#FF9500]" />
                    SKT yaklaşanlar
                  </span>
                  <button onClick={() => setShowExpiring(!showExpiring)} data-testid="expiring-toggle">
                    {showExpiring
                      ? <ToggleRight size={32} className="text-[#FF9500]" />
                      : <ToggleLeft size={32} className="text-[#C7C7CC]" />}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </header>

      <main className="p-4">
        {activeTab === 'operations' && (
          <div className="space-y-4 animate-fade-in">
            {['vaccine', 'treatment', 'service'].map(type => {
              const ops = filteredOps.filter(o => o.type === type);
              if (ops.length === 0) return null;
              const cfg = TYPE_CONFIG[type];
              const Icon = cfg.icon;
              return (
                <div key={type}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-7 h-7 ${cfg.color} rounded-lg flex items-center justify-center`}><Icon size={14} /></div>
                    <span className="text-sm font-semibold text-gray-700">{cfg.label} ({ops.length})</span>
                  </div>
                  <div className="ios-card divide-y divide-gray-100 p-0 overflow-hidden">
                    {ops.map(op => (
                      <div key={op.id} className="px-4 py-3 flex items-center justify-between" data-testid={`clinic-op-${op.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm">{op.name}</p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {op.default_price > 0 && <span className="text-[10px] bg-green-50 px-2 py-0.5 rounded-full text-green-600 font-medium">{op.default_price} TL</span>}
                            {op.default_interval_days && <span className="text-[10px] bg-[#F2F2F7] px-2 py-0.5 rounded-full text-[#8E8E93]">{op.default_interval_days} gün</span>}
                            {op.duration_minutes && <span className="text-[10px] bg-purple-50 px-2 py-0.5 rounded-full text-purple-600">{op.duration_minutes} dk</span>}
                            {op.animal_type !== 'all' && <span className="text-[10px] bg-[#F2F2F7] px-2 py-0.5 rounded-full text-[#8E8E93]">{op.animal_type === 'dog' ? 'Köpek' : 'Kedi'}</span>}
                            {op.linked_products?.length > 0 && <span className="text-[10px] bg-blue-50 px-2 py-0.5 rounded-full text-[#6b6de3]">{op.linked_products.length} ürün</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => openEditOp(op)} className="p-2 rounded-full hover:bg-blue-50 text-[#6b6de3]" data-testid={`edit-op-${op.id}`}><Edit3 size={15} /></button>
                          <button onClick={() => setDeleteConfirm({ type: 'operation', id: op.id, name: op.name })} className="p-2 rounded-full hover:bg-red-100 text-[#8E8E93] hover:text-red-500 transition-colors"><Trash2 size={15} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {filteredOps.length === 0 && (
              <div className="ios-card text-center py-8"><Syringe size={48} className="text-[#C7C7CC] mx-auto mb-3" /><p className="text-[#8E8E93]">{opSearch ? 'Sonuç bulunamadı' : 'Henüz işlem tanımı yok'}</p></div>
            )}
          </div>
        )}

        {activeTab === 'products' && (
          <div className="space-y-4 animate-fade-in">
            {categories.map(cat => {
              const catProducts = filteredProducts.filter(p => p.category_id === cat.id);
              if (catProducts.length === 0) return null;
              return (
                <div key={cat.id}>
                  <span className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider">{cat.name} ({catProducts.length})</span>
                  <div className="ios-card divide-y divide-gray-100 p-0 overflow-hidden mt-2">
                    {catProducts.map(p => <ProductRow key={p.id} product={p} onEdit={setEditingProduct} onDelete={(id, name) => setDeleteConfirm({ type: 'product', id, name })} />)}
                  </div>
                </div>
              );
            })}
            {(() => {
              const uncategorized = filteredProducts.filter(p => !p.category_id);
              if (uncategorized.length === 0) return null;
              return (
                <div>
                  <span className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider">Kategorisiz ({uncategorized.length})</span>
                  <div className="ios-card divide-y divide-gray-100 p-0 overflow-hidden mt-2">
                    {uncategorized.map(p => <ProductRow key={p.id} product={p} onEdit={setEditingProduct} onDelete={(id, name) => setDeleteConfirm({ type: 'product', id, name })} />)}
                  </div>
                </div>
              );
            })()}
            {filteredProducts.length === 0 && (
              <div className="ios-card text-center py-8"><Package size={48} className="text-[#C7C7CC] mx-auto mb-3" /><p className="text-[#8E8E93]">{productSearch ? 'Sonuç bulunamadı' : 'Henüz ürün yok'}</p></div>
            )}
          </div>
        )}

        {activeTab === 'linking' && (
          <div className="space-y-4 animate-fade-in">
            <p className="text-sm text-[#8E8E93]">{'İşlemlere ürün bağlayarak, işlem yapıldığında stoktan otomatik düşürme sağlayabilirsiniz.'}</p>
            {operations.length === 0 ? (
              <div className="ios-card text-center py-8"><Link2 size={48} className="text-[#C7C7CC] mx-auto mb-3" /><p className="text-[#8E8E93]">{'Önce işlem tanımlayın'}</p></div>
            ) : (
              <div className="ios-card divide-y divide-gray-100 p-0 overflow-hidden">
                {operations.map(op => {
                  const cfg = TYPE_CONFIG[op.type] || {};
                  const Icon = cfg.icon || Syringe;
                  const linked = op.linked_products || [];
                  return (
                    <div key={op.id} className="px-4 py-3" data-testid={`link-op-${op.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-6 h-6 ${cfg.color} rounded-md flex items-center justify-center`}><Icon size={12} /></div>
                          <span className="font-medium text-gray-900 text-sm">{op.name}</span>
                        </div>
                        <button onClick={() => { setLinkOp(op); setLinkModal(true); }} className="text-xs text-[#6b6de3] font-medium px-2 py-1 rounded-lg hover:bg-blue-50" data-testid={`link-btn-${op.id}`}>
                          <Link2 size={12} className="inline mr-1" />{`Bağla`}
                        </button>
                      </div>
                      {linked.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {linked.map(lp => (
                            <span key={lp.product_id} className="text-xs bg-blue-50 text-[#6b6de3] px-2 py-1 rounded-full flex items-center gap-1"><Package size={10} /> {lp.product_name}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Modals */}
      <Dialog open={opModal} onOpenChange={setOpModal}>
        <DialogContent className="sm:rounded-2xl sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden flex flex-col p-4 sm:p-6"><DialogHeader><DialogTitle>{`Yeni İşlem`}</DialogTitle></DialogHeader><OperationForm form={opForm} setForm={setOpForm} onSubmit={handleCreateOp} submitLabel="Ekle" /></DialogContent>
      </Dialog>
      <Dialog open={editOpModal} onOpenChange={open => { if (!open) { setEditOpModal(false); setEditOpForm(null); } }}>
        <DialogContent className="sm:rounded-2xl sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden flex flex-col p-4 sm:p-6"><DialogHeader><DialogTitle>{'İşlemi Düzenle'}</DialogTitle></DialogHeader>{editOpForm && <OperationForm form={editOpForm} setForm={setEditOpForm} onSubmit={handleEditOp} submitLabel="Kaydet" />}</DialogContent>
      </Dialog>
      <Dialog open={productModal} onOpenChange={setProductModal}>
        <DialogContent className="sm:rounded-2xl sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden flex flex-col p-4 sm:p-6"><DialogHeader><DialogTitle>{`Yeni Ürün`}</DialogTitle></DialogHeader><ProductForm form={productForm} setForm={setProductForm} categories={categories} onSubmit={handleCreateProduct} submitLabel="Ekle" /></DialogContent>
      </Dialog>
      <Dialog open={!!editingProduct} onOpenChange={open => !open && setEditingProduct(null)}>
        <DialogContent className="sm:rounded-2xl sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden flex flex-col p-4 sm:p-6"><DialogHeader><DialogTitle>{'Ürünü Düzenle'}</DialogTitle></DialogHeader>{editingProduct && <ProductForm form={editingProduct} setForm={setEditingProduct} categories={categories} onSubmit={handleUpdateProduct} submitLabel="Kaydet" />}</DialogContent>
      </Dialog>
      <BatchStockModal open={batchStockModal} onOpenChange={setBatchStockModal} products={products} onRefresh={loadAll} />
      <Dialog open={catModal} onOpenChange={setCatModal}>
        <DialogContent className="sm:rounded-2xl sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden flex flex-col p-4 sm:p-6"><DialogHeader><DialogTitle>Kategoriler</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex gap-2">
              <Input value={catName} onChange={e => setCatName(e.target.value)} placeholder={`Yeni kategori adı`} className="h-10 bg-[#F2F2F7] border-0 rounded-xl flex-1" data-testid="cat-name-input" />
              <Button onClick={handleCreateCategory} size="sm" className="bg-[#6b6de3] rounded-xl h-10 px-4" data-testid="cat-submit-btn">Ekle</Button>
            </div>
            <div className="space-y-1">
              {categories.map(c => (
                <div key={c.id} className="flex items-center justify-between p-2 bg-[#F2F2F7] rounded-lg">
                  <span className="text-sm text-gray-900">{c.name}</span>
                  <button onClick={() => setDeleteConfirm({ type: 'category', id: c.id, name: c.name })} className="p-1 rounded-full hover:bg-red-100 text-[#8E8E93] hover:text-red-500"><Trash2 size={14} /></button>
                </div>
              ))}
              {categories.length === 0 && <p className="text-center text-[#8E8E93] text-sm py-4">{`Henüz kategori yok`}</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={linkModal} onOpenChange={open => { if (!open) { setLinkModal(false); setLinkOp(null); } }}>
        <DialogContent className="sm:rounded-2xl sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden flex flex-col p-4 sm:p-6"><DialogHeader><DialogTitle>{linkOp?.name} {`- Ürün Bağla`}</DialogTitle></DialogHeader>
          {linkOp && (
            <div className="space-y-4 mt-4">
              {(linkOp.linked_products || []).length > 0 && (
                <div>
                  <span className="text-xs text-[#8E8E93] uppercase tracking-wider">{`Bağlı Ürünler`}</span>
                  <div className="space-y-1 mt-2">
                    {linkOp.linked_products.map(lp => (
                      <div key={lp.product_id} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                        <span className="text-sm text-[#6b6de3] font-medium">{lp.product_name}</span>
                        <button onClick={() => handleUnlinkProduct(lp.product_id)} className="p-1 rounded-full hover:bg-red-100 text-[#8E8E93] hover:text-red-500"><X size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <span className="text-xs text-[#8E8E93] uppercase tracking-wider">{'Ürün Ekle'}</span>
                <div className="space-y-1 mt-2 max-h-60 overflow-y-auto">
                  {products.filter(p => !(linkOp.linked_products || []).find(lp => lp.product_id === p.id)).map(p => (
                    <button key={p.id} onClick={() => handleLinkProduct(p.id)} className="w-full flex items-center justify-between p-2 bg-[#F2F2F7] rounded-lg hover:bg-gray-200 transition-colors" data-testid={`link-product-${p.id}`}>
                      <div className="text-left"><span className="text-sm text-gray-900">{p.name}</span>{p.category_name && <span className="text-xs text-[#8E8E93] ml-2">{p.category_name}</span>}</div>
                      <Plus size={16} className="text-[#6b6de3]" />
                    </button>
                  ))}
                  {products.length === 0 && <p className="text-center text-[#8E8E93] text-sm py-4">{'Önce ürün ekleyin'}</p>}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!deleteConfirm} onOpenChange={open => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>{`Silmek istediğinize emin misiniz?`}</AlertDialogTitle><AlertDialogDescription>{deleteConfirm && <><strong>{deleteConfirm.name}</strong> silinecek.</>}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-full">{`Vazgeç`}</AlertDialogCancel><AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-500 hover:bg-red-600 rounded-full">Sil</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <BottomNav />
    </div>
  );
};

export default Stock;
